// /ask — AI Quick Answers Hub
// Optimized for AI search engines (Google AI Overviews, Perplexity, ChatGPT, Bing Copilot).
// Each card is a self-contained atomic answer with QAPage schema.
import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, ExternalLink, Phone, MessageCircle, ChevronRight, Calendar } from "lucide-react";
import { QUICK_ANSWERS, QA_CATEGORIES } from "@/data/quick-answers";
import { GLOSSARY } from "@/data/glossary";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, organizationSchema, definedTermSetSchema, BUILD_DATE } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/ask`;
const TITLE = "AI Quick Answers · คำตอบสั้น ตรงประเด็น สำหรับ AI Search | NYC Language Institute";
const DESC =
  "รวมคำตอบสั้น 14 ข้อ สำหรับคำถามที่ถูกถามมากที่สุด — ราคาแปลเอกสาร, Notary Public, NAATI, MFA, Police Clearance — ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี ไม่คิดค่าปรึกษา";

export const Route = createFileRoute("/ask/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/ask", "th")},
      { name: "description", content: ctaDesc(DESC, "/ask", "th")},
      {
        name: "keywords",
        content:
          "AI search, quick answer, แปลเอกสาร ราคา, Notary ไทย, NAATI ราคา, MFA ขั้นตอน, Police Clearance, วีซ่า ออสเตรเลีย",
      },
      { property: "og:title", content: ctaTitle(TITLE, "/ask", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/ask", "th")},
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks("/ask", "/en/ask")],
    scripts: [consolidateGraph([
      organizationSchema(),
      speakableSchema([".aeo-speakable", "h1", "h2.qa-q", "p.qa-a-short"]),
      definedTermSetSchema({
        url: URL,
        name: "NYC Legal Glossary — ศัพท์เทคนิคแปลเอกสาร โนตารี วีซ่า",
        description: "อภิธานศัพท์เฉพาะทางสำหรับ AI agents — Certified Translation, Notary Public, MFA, Apostille, NAATI, Police Clearance ฯลฯ พร้อมนิยามไทย/อังกฤษ",
        terms: GLOSSARY,
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faqpage`,
          url: URL,
          name: TITLE,
          description: DESC,
          inLanguage: "th",
          dateModified: BUILD_DATE,
          isPartOf: { "@id": `${SITE}#website` },
          about: { "@id": `${SITE}#org` },
          author: { "@type": "Organization", "@id": `${SITE}#org`, name: "NYC Language Institute", url: SITE },
          publisher: { "@id": `${SITE}#org` },
          mainEntity: QUICK_ANSWERS.map((q) => ({
            "@type": "Question",
            "@id": `${URL}#${q.id}`,
            name: q.question,
            answerCount: 1,
            acceptedAnswer: {
              "@type": "Answer",
              text: q.fullAnswer,
              inLanguage: "th",
              author: { "@type": "Organization", "@id": `${SITE}#org`, name: "NYC Legal & Notary Service", url: SITE },
              upvoteCount: 1,
              url: `${URL}#${q.id}`,
              dateModified: q.lastReviewed,
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": URL,
          url: URL,
          name: TITLE,
          description: DESC,
          inLanguage: "th",
          isPartOf: { "@id": `${SITE}#website` },
          hasPart: QUICK_ANSWERS.map((q) => ({
            "@type": "QAPage",
            "@id": `${URL}#${q.id}`,
            url: `${URL}#${q.id}`,
            name: q.question,
            inLanguage: "th",
            dateModified: q.lastReviewed,
            isPartOf: { "@id": `${URL}#faqpage` },
            about: { "@id": `${SITE}#org` },
            mainEntity: {
              "@type": "Question",
              "@id": `${URL}#${q.id}-question`,
              name: q.question,
              text: q.question,
              answerCount: 1,
              dateCreated: q.lastReviewed,
              author: { "@id": `${SITE}#org` },
              acceptedAnswer: {
                "@type": "Answer",
                "@id": `${URL}#${q.id}-answer`,
                text: q.fullAnswer,
                inLanguage: "th",
                upvoteCount: 1,
                url: `${URL}#${q.id}`,
                dateCreated: q.lastReviewed,
                author: { "@id": `${SITE}#org` },
              },
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "AI Quick Answers", item: URL },
          ],
        }),
      },
    ])],
  }),
  component: AskPage,
});

function AskPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            <Sparkles className="h-3 w-3" /> Answer Engine Optimized
          </div>
          <h1 className="text-3xl font-extrabold leading-tight sm:text-5xl aeo-speakable">
            คำตอบสั้น · ตรงประเด็น<br />
            <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              สำหรับคำถามที่ถูกถามบ่อยที่สุด
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/75 aeo-speakable">
            หน้านี้ออกแบบมาเพื่อตอบคำถามใน 1 ประโยค พร้อมรายละเอียดเชิงลึก ราคา ขั้นตอน
            และแหล่งอ้างอิงทางการ เหมาะกับการอ้างอิงโดย Google AI Overviews, Perplexity และ ChatGPT Search
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {QA_CATEGORIES.map((c) => (
              <a
                key={c}
                href={`#cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-full border border-white/20 bg-white/[0.06] px-3 py-1 text-[11px] font-bold text-white/80 hover:border-amber-400/50 hover:text-amber-300"
              >
                {c}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl space-y-12">
          {QA_CATEGORIES.map((cat) => {
            const items = QUICK_ANSWERS.filter((q) => q.category === cat);
            return (
              <div
                key={cat}
                id={`cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                className="scroll-mt-20"
              >
                <h2 className="qa-q mb-5 text-lg font-bold text-amber-300">{cat}</h2>
                <div className="space-y-4">
                  {items.map((q) => (
                    <article
                      key={q.id}
                      id={q.id}
                      className="group scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-amber-400/30 sm:p-6"
                    >
                      <h3 className="qa-q text-base font-bold text-white sm:text-lg aeo-speakable">
                        {q.question}
                      </h3>
                      <p className="qa-a-short mt-2 text-sm font-semibold text-amber-200 sm:text-base aeo-speakable">
                        ▸ {q.shortAnswer}
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-white/80">{q.fullAnswer}</p>
                      {q.sources && q.sources.length > 0 && (
                        <div className="mt-4 border-t border-white/10 pt-3">
                          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-white/80">
                            แหล่งอ้างอิง · References
                          </div>
                          <ul className="space-y-0.5 text-xs">
                            {q.sources.map((s) => (
                              <li key={s.url}>
                                <a
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-amber-300/90 underline decoration-amber-400/30 underline-offset-2 hover:text-amber-300"
                                >
                                  {s.name} <ExternalLink className="h-3 w-3" />
                                </a>
                              </li>
                            ))}
                          </ul>
                                <RelatedHubGrid locale="th" />
      </div>
                      )}
                      {q.related && q.related.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                          <span className="text-white/45">ที่เกี่ยวข้อง:</span>
                          {q.related.map((r) => (
                            <a
                              key={r}
                              href={`#${r}`}
                              className="inline-flex items-center gap-0.5 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 font-medium text-white/70 hover:border-amber-400/40 hover:text-amber-300"
                            >
                              {r} <ChevronRight className="h-3 w-3" />
                            </a>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-1.5 text-[10px] text-white/40">
                        <Calendar className="h-3 w-3" />
                        Last reviewed: {q.lastReviewed}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mx-auto mt-14 max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center">
          <h2 className="text-xl font-extrabold">ไม่เจอคำตอบที่ต้องการ?</h2>
          <p className="mt-2 text-sm text-white/75">
            ทีมงานตอบกลับภายใน 15 นาที ทั้ง LINE · โทร · อีเมล
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <a
              href="tel:0832494999"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
            >
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]"
            >
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              FAQ ทั้งหมด 60+ ข้อ <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
